import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react'
import { io } from "socket.io-client"

// components
import AppChatDetails from 'src/components/details/chat-details/AppChatDetails'
import Axios from 'src/helpers/axios'
import getServerURL from 'src/helpers/get-server-url'

// modal
import AppModalFilePreview from 'src/components/modals/AppModalFilePreview';
import AppModalSendFilePreview from 'src/components/modals/AppModalSendFilePreview';

// helpers
import { formatFileSize } from 'src/helpers/text-format';

const Chat = () => {
  const [chatRooms, setChatRooms] = useState([])
  const [activeChatRoom, setActiveChatRoom] = useState(null)
  const [activeChatRoomId, setActiveChatRoomId] = useState(null)
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [fileCaption, setFileCaption] = useState('');

  const [chatHeight, setChatHeight] = useState('100%')

  useLayoutEffect(() => {
    const body = document.querySelector('.body')
    const rect = body.getBoundingClientRect()

    setChatHeight(`${rect.height}px`)
  }, [])
  
  const socketRef = useRef(null)
  const endOfMessageRef = useRef(null)
  const activeChatRoomIdRef = useRef(null)
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    type: null, // 'image', 'document', 'video'
    url: null,
    fileName: null
  });

  /**
   * Keep active room id in ref (for socket callbacks)
   */
  useEffect(() => {
    activeChatRoomIdRef.current = activeChatRoomId
  }, [activeChatRoomId])

  /**
   * Scroll helper
   */
  const scrollToBottom = useCallback(() => {
    if (endOfMessageRef?.current) {
      setTimeout(
        () => endOfMessageRef.current?.scrollIntoView({ behavior: 'smooth' }),
        10
      )
    }
  }, [])

  /**
   * Fetch chat rooms (initial & search)
   */
  const fetchChatRooms = useCallback(async () => {
    try {
      const q = searchQuery ? `?search=${searchQuery}` : ''
      const response = await Axios.get(`/api/whatsapp/v1/contact${q}`)
      const contactList = response?.data?.value || []
      
      if (Array.isArray(contactList) && contactList.length > 0) {
        const contactWithMessage = contactList?.filter(item => item?.messages?.length > 0) || []
        setChatRooms(contactWithMessage)
      }
    } catch (error) {
      console.error('Error fetching chat rooms:', error)
    }
  }, [searchQuery])

  /**
   * SOCKET INITIALIZATION
   */
  useEffect(() => {
    socketRef.current = io(getServerURL(), {
      transports: ['websocket'],
    })

    /**
     * CONNECT
     */
    socketRef.current.on('connect', () => {
      console.log('[SOCKET] connected')

      // Join admin room (tanpa userId)
      socketRef.current.emit('admin:join')
    })

    /**
     * DISCONNECT
     */
    socketRef.current.on('disconnect', () => {
      console.log('[SOCKET] disconnected')
    })

    /**
     * CONTACT CREATED
     */
    socketRef.current.on('contact:new', (contact) => {
      setChatRooms((prev) => [contact, ...prev])
    })

    /**
     * CONTACT UPDATED
     */
    socketRef.current.on('contact:update', (updatedContact) => {
      let newChatRooms = updatedContact?.contacts || []
      setChatRooms(newChatRooms)
    })

    /**
     * NEW MESSAGE
     */
    socketRef.current.on('message:new', (data) => {
      let message = data?.message
      if (!message) return

      const activeId = activeChatRoomIdRef.current

      // Update active chat messages
      if (activeId && message.contact_id == activeId) {
        setActiveChatRoom((prev) => ({
          ...prev,
          messages: [...(prev?.messages || []), message],
        }))
        scrollToBottom()

        socketRef.current.emit('chat:join', {
          contactId: activeId,
        })
      }

      // Update chat list last message
      setChatRooms((prev) => {
        const updated = prev.map((room) =>
          room.id === message.contact_id
            ? {
                ...room,
                lastMessage: message.text,
                last_message_at: message.timestamp,
                last_message_type: message.type,
              }
            : room
        )

        return updated.sort(
          (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
        )
      })
    })

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('admin:leave')
        socketRef.current.disconnect()
      }
    }
  }, [scrollToBottom])

  /**
   * Initial load
   */
  useEffect(() => {
    fetchChatRooms()
  }, [fetchChatRooms])

  /**
   * OPEN CHAT ROOM
   */
  const handleOpenRoom = (roomId) => {
    const room = chatRooms.find((r) => r.id === roomId)
    if (!room) return

    // Leave previous room
    if (activeChatRoomIdRef.current) {
      socketRef.current.emit('chat:leave', {
        contactId: activeChatRoomIdRef.current,
      })
    }

    setActiveChatRoom(room)
    setActiveChatRoomId(roomId)

    // Join new chat room
    socketRef.current.emit('chat:join', {
      contactId: roomId,
    })

    socketRef.current.on('chat:joined', ({ contacts }) => {
      setChatRooms(contacts)
    })

    scrollToBottom()
  }

  const handleInputValue = () => {
    const formData = new FormData()
    formData.append('text', newMessage)
    formData.append('recipient', activeChatRoom?.wa_id)
    formData.append('send_type', "regular")
    if (previewFile?.file) {
      formData.append('file', previewFile.file)
      formData.append('message_type', previewType)
      if (fileCaption) {
        formData.append('file_caption', fileCaption)
      }
    } else {
      formData.append('message_type', 'text')
    }

    return formData
  }

  const handleSave = (isText) => {
    const inputPayload = handleInputValue()
    handleSendMessage(inputPayload, isText)
  }

  /**
   * SEND MESSAGE (optimistic UI)
   */
  const handleSendMessage = async (payload, isText = true) => {
    if (isText) {
      if (!newMessage.trim() || !activeChatRoom) return
    }

    setNewMessage('')
    scrollToBottom()

    try {
      Axios({
        method: "post",
        url: `/api/whatsapp/v1/message/send`,
        data: payload
      })
      .then(({ data }) => {
        if (data?.status == 'success') {
            console.log('Message sent successfully')
            let resMessage = data?.value
            
            // Optimistic update
            setActiveChatRoom((prev) => ({
              ...prev,
              messages: [...(prev?.messages || []), resMessage],
            }))
        
            setChatRooms((prev) =>
              prev.map((room) =>
                room.id == activeChatRoom.id
                  ? {
                      ...room,
                      last_message: resMessage?.text,
                      last_message_at: resMessage.timestamp,
                      last_message_type: resMessage.type,
                    }
                  : room
              )
            )
          }
        })
        .catch((err) => {
          console.error(err);
        })
    } catch (error) {
      console.error('Failed to send message')

      console.log(error.response?.data || error.message)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave(true)
    }
  }

  const openPreview = (type, url, fileName = null) => {
    setPreviewModal({
      isOpen: true,
      type,
      url,
      fileName
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      type: null,
      url: null,
      fileName: null
    });
  };

  const handleAttachmentClick = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
  };

  const handleImageSelect = () => {
    imageInputRef.current?.click();
    setShowAttachmentMenu(false);
  };

  const handleDocumentSelect = () => {
    documentInputRef.current?.click();
    setShowAttachmentMenu(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewFile({
          file: file,
          preview: reader.result,
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type
        });
        setPreviewType(file.type.startsWith('image/') ? 'image' : 'video');
        setShowPreviewModal(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    e.target.value = '';
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewFile({
        file: file,
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type
      });
      setPreviewType('document');
      setShowPreviewModal(true);
    }
    // Reset input
    e.target.value = '';
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewFile(null);
    setFileCaption('');
    setPreviewType(null);
  };

  const handleSendFile = () => {
    if (previewFile) {
      // Handle file upload logic here
      console.log('Sending file:', previewFile);
      console.log('Caption:', fileCaption);

      handleSave(false);
      
      // Example: You can call your API here
      // uploadFile(previewFile.file, fileCaption);
      
      handleClosePreviewModal();
    }
  };

  // Close attachment menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAttachmentMenu && !event.target.closest('.attachment-menu-container')) {
        setShowAttachmentMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAttachmentMenu]);

  const handleDeleteChat = (contactId) => {
    Axios({
      method: "delete",
      url: `/api/whatsapp/v1/message/${contactId}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setChatRooms(prev => prev.filter(room => room.id !== activeChatRoom?.id));
          setActiveChatRoom(null);
          setActiveChatRoomId(null);
          setShowModal(false);
        }
      })
      .catch((err) => {
        console.error(err);
      })
  }

  return (
    <div
      style={{
        height: chatHeight,
        overflow: 'hidden'
      }}
    >
      {
        previewModal.isOpen && (
          <AppModalFilePreview
            previewModal={previewModal}
            closePreview={closePreview}
          />
        )
      }
      {
        showPreviewModal && (
          <AppModalSendFilePreview
            handleClosePreviewModal={handleClosePreviewModal}
            previewFile={previewFile}
            previewType={previewType}
            fileCaption={fileCaption}
            setFileCaption={setFileCaption}
            handleSendFile={handleSendFile}
          />
        )
      }
      <AppChatDetails
        activeChatRoom={activeChatRoom}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        chatRooms={chatRooms}
        showModal={showModal}
        setShowModal={setShowModal}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        endOfMessageRef={endOfMessageRef}
        handleOpenRoom={handleOpenRoom}
        handleKeyDown={handleKeyDown}
        handleSendMessage={handleSave}
        openPreview={openPreview}
        handleAttachmentClick={handleAttachmentClick}
        handleImageSelect={handleImageSelect}
        handleDocumentSelect={handleDocumentSelect}
        imageInputRef={imageInputRef}
        documentInputRef={documentInputRef}
        handleImageChange={handleImageChange}
        handleDocumentChange={handleDocumentChange}
        showAttachmentMenu={showAttachmentMenu}
        handleDeleteChat={handleDeleteChat}
      />
    </div>
  )
}

export default Chat
